/**
 * The CV Health editor sheet's state machine, shared by `CvHealthSheet.tsx`
 * (native BottomSheet) and `CvHealthSheet.web.tsx` (WebDialog). The twins
 * only render; every decision, save, undo, recheck and analytics event lives
 * here so the two platforms can never drift.
 *
 * Views (one sheet whose body swaps, never a second modal):
 *
 *   overview ──Review N suggestions / a suggestion row──▶ review ──Save──┐
 *      │  ▲                                                               │
 *      │  └──────────── "N changes saved · Undo" on the overview ◀────────┘
 *      ├──What we checked ▸ area──▶ area (tabs) ──Preview N changes──▶ review
 *      └──Add a work link──▶ proposal (one link) ──Save──▶ overview
 *
 * The overview is the 30 Sep 2026 design: the score card (gauge, and the
 * capsule "Review N suggestions" / "Continue reviewing", or "All checks
 * passed"), the saved notice with Undo, "View individual suggestions" and
 * "What we checked". The review lists the proposed changes with a checkbox
 * and an editable Suggested field each; Save writes only the ticked ones,
 * with the user's wording, in one server call. Going back keeps the ticks and
 * edits ("Continue reviewing"); "Keep original" drops them and records
 * nothing (a row's own "Leave unchanged" / "Skip for now" is the lasting
 * decision).
 *
 * Every change is written by the server: Save applies the selected items,
 * Undo sends the receipt back. Each answers with the rechecked report, which
 * this controller follows until it settles (the gauge then moves from the
 * old score to the new one); the CV queries are invalidated so the editor
 * shows the new text.
 *
 * Reviews start ONLY from `handleCheck` and `handleRecheck`, plus the
 * automatic recheck of a changed CV that the dock owns (`useCvHealth`).
 * Opening the sheet never starts one. Back steps one view; Close / swipe /
 * backdrop dismiss the sheet. Edited, unsaved text asks "Keep editing /
 * Discard changes" first.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type {
  CvHealthArea,
  CvHealthDecision,
  CvHealthFinding,
  CvHealthReceipt,
  CvHealthReport,
  CvHealthReportEnvelope,
  CvHealthTrigger,
} from "@tabbio/types";
import { useCvHealth } from "../../hooks/useCvHealth";
import { useCvHealthReview } from "../../hooks/useCvHealthReview";
import {
  applyCvHealthChanges,
  CV_HEALTH_NOT_FOUND,
  recordCvHealthDecision,
  undoCvHealthChanges,
} from "../../lib/cv-health-api";
import {
  countSuggestions,
  isReportUsable,
  toCountBand,
  toScoreBand,
} from "../../lib/cv-health-model";
import {
  buildProofLinkFinding,
  normalizeCvHealthLink,
  toCvHealthTargetKind,
} from "../../lib/cv-health-editor";
import {
  countSelectedDrafts,
  createReviewDrafts,
  hasEditedDrafts,
  toApplyItems,
  type CvHealthReviewDraft,
} from "../../lib/cv-health-review";
import { invalidateCvDocumentQueries } from "../../lib/cv-query-invalidation";
import { trackProductEvent } from "../../lib/product-analytics";
import { getFixableFindings, getSuggestionFindings } from "../cv-health/cv-health-copy";
import { noteAreaBack } from "../cv-health/health-disclosure-memory";

export type CvHealthSheetView = "overview" | "area" | "review" | "proposal";

/** What the overview shows. `stale` is only reached when the automatic recheck failed. */
export type CvHealthOverviewMode = "empty" | "checking" | "failed" | "stale" | "result";

/** The saved notice's recheck state. */
export type CvHealthRecheckStatus = "checking" | "updated" | "failed";

/** Inline error of the review or the link view. `skipped`: the server skipped the change (the CV changed). */
export type CvHealthSaveError = "saveFailed" | "invalidLink" | "skipped" | "empty";

/** Which proposals a review covers: every one, one area's, or one finding's. */
export type CvHealthReviewScope = "all" | "area" | "single";

export interface CvHealthSheetReceipt {
  receipt: CvHealthReceipt;
  /** The report the changes were made from: the gauge starts here. */
  baseReport: CvHealthReport;
  undone: boolean;
  /** Undo could not restore these many changes: the CV changed again since. */
  undoSkipped: number;
}

const ENTRY_POINT = "editor";

const RECHECK_TRIGGERS: readonly CvHealthTrigger[] = [
  "editor_recheck",
  "editor_apply",
  "editor_undo",
];

function trackFindingAction(action: string, finding: CvHealthFinding) {
  trackProductEvent("cv_health_finding_action", {
    entry_point: ENTRY_POINT,
    action,
    check_id: finding.checkIds[0] ?? null,
    area: finding.area,
  });
}

export interface UseCvHealthSheetControllerOptions {
  visible: boolean;
  onClose: () => void;
  /** Platform prompt for "Discard your edits?"; resolves true to discard. */
  confirmDiscard: () => Promise<boolean>;
}

export function useCvHealthSheetController({
  visible,
  onClose,
  confirmDiscard,
}: UseCvHealthSheetControllerOptions) {
  const { i18n } = useTranslation();
  const locale: "en" | "ar" = i18n.language?.startsWith("ar") ? "ar" : "en";
  const queryClient = useQueryClient();
  const health = useCvHealth();

  const [view, setView] = useState<CvHealthSheetView>("overview");
  const [area, setArea] = useState<CvHealthArea>("human");
  /** The view the review or the link view returns to. */
  const [returnView, setReturnView] = useState<"overview" | "area">("overview");

  // Review: the proposals under review and the user's ticks and edits.
  const [reviewScope, setReviewScope] = useState<CvHealthReviewScope>("all");
  const [reviewFindings, setReviewFindings] = useState<CvHealthFinding[]>([]);
  /**
   * The report the review (or the link view) was opened from. Save sends its
   * id, so the server applies the wording the user saw, never a newer report's.
   */
  const [reviewReport, setReviewReport] = useState<CvHealthReport | null>(null);
  const [drafts, setDrafts] = useState<CvHealthReviewDraft[]>([]);
  /** A full review left with Back: the card offers "Continue reviewing". */
  const [reviewPending, setReviewPending] = useState(false);

  // The one-link view (Add a work link).
  const [finding, setFinding] = useState<CvHealthFinding | null>(null);
  const [linkDraft, setLinkDraft] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<CvHealthSaveError | null>(null);
  const [errorFindingKey, setErrorFindingKey] = useState<string | null>(null);
  /** Bumped by each failed Save, so the same error opens and focuses its change again. */
  const [errorNonce, setErrorNonce] = useState(0);
  /** How many changes the `skipped` error is about. */
  const [skippedErrorCount, setSkippedErrorCount] = useState(0);
  const [busyFindingKey, setBusyFindingKey] = useState<string | null>(null);
  /** Suggestions the last save could not apply because the CV changed. */
  const [skippedCount, setSkippedCount] = useState(0);
  const [receipt, setReceipt] = useState<CvHealthSheetReceipt | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undoFailed, setUndoFailed] = useState(false);
  /**
   * The review running now follows a server write (Save or Undo), so the
   * overview keeps the report the change came from until it settles. A
   * "Check again" the user starts never does.
   */
  const [followingWrite, setFollowingWrite] = useState(false);
  // The overview's two disclosures, kept here so an area's tabs and Back
  // (which swap the overview out) leave them as they were.
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [checkedOpen, setCheckedOpen] = useState(false);

  /** Bumped by `reset`, so a save that lands after the sheet closed stays quiet. */
  const sessionRef = useRef(0);
  const triggerRef = useRef<CvHealthTrigger | null>(null);
  /** True from the tap until the apply call answers: a second tap never sends a second apply. */
  const savingRef = useRef(false);
  /** A save whose recheck has not settled yet, for `cv_health_fix_completed`. */
  const pendingFixRef = useRef<{ receipt: CvHealthReceipt; baseReport: CvHealthReport } | null>(null);
  const viewedReportIdsRef = useRef(new Set<string>());
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const { write: writeLatest, writeReport, refetch: refetchLatest, target } = health;

  const review = useCvHealthReview({
    onSettled: (envelope: CvHealthReportEnvelope) => {
      writeLatest(envelope);
      refetchLatest();
      const trigger = triggerRef.current;
      if (trigger && RECHECK_TRIGGERS.includes(trigger)) {
        trackProductEvent("cv_health_recheck_completed", {
          entry_point: ENTRY_POINT,
          status: envelope.report.status,
          score_band: toScoreBand(envelope.report.score),
        });
      }
      const fix = pendingFixRef.current;
      if (fix && trigger === "editor_apply") {
        pendingFixRef.current = null;
        trackProductEvent("cv_health_fix_completed", {
          entry_point: ENTRY_POINT,
          applied_count: fix.receipt.items.length,
          skipped_count: fix.receipt.skipped.length,
          score_before_band: toScoreBand(fix.baseReport.score),
          score_after_band: toScoreBand(envelope.report.score),
        });
      }
    },
    onDisabled: () => {
      // The flag went off: the latest query learns it, the dock falls back to
      // the setup ring, and this sheet gets out of the way.
      refetchLatest();
      onCloseRef.current();
    },
  });

  const reviewPhaseRef = useRef(review.phase);
  reviewPhaseRef.current = review.phase;
  const { reset: resetReview, adopt: adoptReview, start: startReview } = review;

  // Every poll of a review this sheet follows lands in the shared cache, so
  // the dock control follows the run even after the sheet closes.
  const reviewEnvelope = review.envelope;
  useEffect(() => {
    if (reviewEnvelope) writeLatest(reviewEnvelope);
  }, [reviewEnvelope, writeLatest]);

  const report = health.report;
  const reviewActive = review.phase === "starting" || review.phase === "running";
  // A changed CV is rechecked on its own (the dock owns it); until that
  // starts, and while it runs, the sheet shows the checking state.
  const autoRecheckPending = health.isStale && !health.autoRecheckFailed;
  /**
   * The recheck after a save or Undo gave up. The running report it followed
   * stays in the shared cache, so that alone is no longer a check in progress.
   */
  const followedRecheckFailed = review.phase === "failed" && receipt !== null;
  // Tell the dock too: it reads the same cached running report.
  const { abandonRun } = health;
  const abandonedReportId =
    followedRecheckFailed && report?.status === "running" ? report.id : null;
  useEffect(() => {
    if (abandonedReportId) abandonRun(abandonedReportId);
  }, [abandonRun, abandonedReportId]);
  /** A newer settled report arrived after that (the dock's own refresh): it stands. */
  const recheckRecovered =
    followedRecheckFailed &&
    report !== null &&
    report.status !== "running" &&
    report.id !== receipt.baseReport.id &&
    isReportUsable(report);
  const checking =
    reviewActive ||
    (report?.status === "running" && !followedRecheckFailed) ||
    autoRecheckPending;
  /** Only a running report may feed the checking progress. */
  const checkingReport: CvHealthReport | null =
    review.report?.status === "running"
      ? review.report
      : report?.status === "running"
        ? report
        : null;

  const overviewMode: CvHealthOverviewMode = checking
    ? "checking"
    : review.phase === "failed" && !recheckRecovered
      ? "failed"
      : !report
        ? "empty"
        : health.isStale
          ? "stale"
          : "result";

  const stage = reviewActive ? review.stage : (checkingReport?.stage ?? "reading");
  const fixableFindings = useMemo(
    () => (overviewMode === "result" ? getFixableFindings(report) : []),
    [overviewMode, report],
  );

  const recheckStatus: CvHealthRecheckStatus = reviewActive
    ? "checking"
    : review.phase === "failed" && !recheckRecovered
      ? "failed"
      : "updated";

  /**
   * A saved change is rechecking: the overview may draw the report it came
   * from, whose suggestions were just applied, so none of them may open a review.
   */
  const rechecking = receipt !== null && recheckStatus === "checking";
  /**
   * The report the overview card draws. Right after a save or Undo, while the
   * recheck runs, it stays the report the change came from (with "Checking
   * your changes…"), so the gauge then moves from that score to the new one.
   * A recheck the user asked for draws the latest report instead: by then the
   * base one no longer describes the CV.
   */
  const overviewReport: CvHealthReport | null =
    receipt && rechecking && followingWrite ? receipt.baseReport : report;
  /** The overview shows the result (not the checking steps) while a saved change rechecks. */
  const showsResult =
    overviewMode === "result" || (receipt !== null && overviewReport !== null && isReportUsable(overviewReport));

  /** Edited review text (open, or paused for "Continue reviewing") or link text. */
  const reviewEdited = hasEditedDrafts(reviewFindings, drafts);
  const isDirty =
    ((view === "review" || reviewPending) && reviewEdited) ||
    (view === "proposal" && linkDraft !== null && linkDraft.trim() !== "");

  // ── Analytics: one "viewed" per report per open ──────────────────────
  useEffect(() => {
    if (!visible) {
      viewedReportIdsRef.current = new Set();
      return;
    }
    if (view !== "overview" || overviewMode !== "result" || !report) return;
    if (viewedReportIdsRef.current.has(report.id)) return;
    viewedReportIdsRef.current.add(report.id);
    trackProductEvent("cv_health_report_viewed", {
      entry_point: ENTRY_POINT,
      status: report.status,
      score_band: toScoreBand(report.score),
      count_band: toCountBand(countSuggestions(report)),
    });
  }, [overviewMode, report, view, visible]);

  // ── Reviews: started only by these two handlers ──────────────────────
  const reviewInput = useCallback(
    (trigger: CvHealthTrigger) => {
      triggerRef.current = trigger;
      trackProductEvent("cv_health_review_started", { entry_point: ENTRY_POINT, trigger });
      return { target, trigger, locale };
    },
    [locale, target],
  );

  const handleCheck = useCallback(() => {
    setFollowingWrite(false);
    startReview(reviewInput("editor_open"));
  }, [reviewInput, startReview]);

  const handleRecheck = useCallback(() => {
    setFollowingWrite(false);
    startReview(reviewInput("editor_recheck"));
  }, [reviewInput, startReview]);

  /**
   * The server changed the CV: refresh every editor query (the preview shows
   * the new text), put the rechecked report in the cache, and follow it until
   * it settles. Runs even when the sheet closed meanwhile.
   */
  const followServerWrite = useCallback(
    (envelope: CvHealthReportEnvelope, trigger: CvHealthTrigger) => {
      writeLatest(envelope);
      void invalidateCvDocumentQueries(queryClient, target);
      triggerRef.current = trigger;
      setFollowingWrite(true);
      adoptReview(envelope);
    },
    [adoptReview, queryClient, target, writeLatest],
  );

  // ── Decisions (non-proposal findings expanded in place) ─────────────
  const decide = useCallback(
    async (next: CvHealthFinding, decision: CvHealthDecision) => {
      // A row decides against the report on screen, never one a save is rechecking.
      const from = rechecking ? null : report;
      if (!from || busyFindingKey) return;
      setBusyFindingKey(next.key);
      trackFindingAction(decision, next);
      try {
        const response = await recordCvHealthDecision({
          reportId: from.id,
          findingKey: next.key,
          decision,
        });
        if (response.data?.report) writeReport(response.data.report);
        // A fresh review replaced that report (same CV, a retried AI pass):
        // show the current one rather than an error.
        else if (response.error?.code === CV_HEALTH_NOT_FOUND) refetchLatest();
      } finally {
        setBusyFindingKey(null);
      }
    },
    [busyFindingKey, rechecking, refetchLatest, report, writeReport],
  );

  // ── Review: pick and edit, then save the ticked changes ─────────────
  const clearSaveError = useCallback(() => {
    setSaveError(null);
    setErrorFindingKey(null);
  }, []);

  const openReview = useCallback(
    async (
      scope: CvHealthReviewScope,
      findings: CvHealthFinding[],
      from: "overview" | "area",
      source: CvHealthReport,
    ) => {
      if (findings.length === 0) return;
      const resume = scope === "all" && reviewPending;
      // A single suggestion or an area replaces a paused full review: its edits ask first.
      if (!resume && reviewPending && reviewEdited && !(await confirmDiscard())) return;
      setReviewPending(false);
      setDrafts((current) =>
        createReviewDrafts(findings, resume ? current : [], resume ? reviewFindings : []),
      );
      setReviewFindings(findings);
      setReviewReport(source);
      setReviewScope(scope);
      setReturnView(from);
      clearSaveError();
      setView("review");
      trackProductEvent("cv_health_review_opened", {
        entry_point: ENTRY_POINT,
        scope,
        resumed: resume,
        count_band: toCountBand(findings.length),
      });
    },
    [clearSaveError, confirmDiscard, reviewEdited, reviewFindings, reviewPending],
  );

  /** "Review N suggestions" / "Continue reviewing" on the card. */
  const reviewAll = useCallback(() => {
    if (!report || rechecking) return;
    void openReview("all", getFixableFindings(report), "overview", report);
  }, [openReview, rechecking, report]);

  /** "Preview N changes" on an area tab. */
  const reviewArea = useCallback(
    (next: CvHealthArea) => {
      if (!report || rechecking) return;
      void openReview(
        "area",
        getFixableFindings(report).filter((item) => item.area === next),
        "area",
        report,
      );
    },
    [openReview, rechecking, report],
  );

  /** One suggestion row: its editable comparison, directly. */
  const previewFinding = useCallback(
    (next: CvHealthFinding) => {
      if (!report || rechecking) return;
      trackFindingAction("preview", next);
      void openReview("single", [next], view === "area" ? "area" : "overview", report);
    },
    [openReview, rechecking, report, view],
  );

  const toggleDraft = useCallback(
    (findingKey: string) => {
      clearSaveError();
      setDrafts((current) =>
        current.map((draft) =>
          draft.findingKey === findingKey ? { ...draft, include: !draft.include } : draft,
        ),
      );
    },
    [clearSaveError],
  );

  const setDraftText = useCallback(
    (findingKey: string, text: string) => {
      clearSaveError();
      setDrafts((current) =>
        current.map((draft) => (draft.findingKey === findingKey ? { ...draft, text } : draft)),
      );
    },
    [clearSaveError],
  );

  const leaveReview = useCallback(
    (keep: boolean) => {
      // Only a review of every suggestion is resumable; a single one or an
      // area's is a quick look.
      setReviewPending(keep && reviewScope === "all");
      if (!keep || reviewScope !== "all") setDrafts([]);
      clearSaveError();
      setView(returnView);
    },
    [clearSaveError, returnView, reviewScope],
  );

  /** Saved (or nothing could be saved): back where the review came from, with the notice. */
  const settleSave = useCallback(
    (result: { receipt: CvHealthReceipt } | null, baseReport: CvHealthReport) => {
      setSaving(false);
      setReviewPending(false);
      setDrafts([]);
      setReviewFindings([]);
      setReviewReport(null);
      if (result) {
        setReceipt({ receipt: result.receipt, baseReport, undone: false, undoSkipped: 0 });
        setUndoFailed(false);
      }
      setView("overview");
    },
    [],
  );

  /** Save: every ticked change, with the user's wording, in one server call. */
  const saveReview = useCallback(async () => {
    if (!reviewReport || savingRef.current) return;
    const built = toApplyItems(reviewFindings, drafts);
    if (!built.ok) {
      setSaveError(built.error);
      setErrorFindingKey(built.findingKey);
      setErrorNonce((current) => current + 1);
      return;
    }
    if (built.items.length === 0) return;
    savingRef.current = true;
    const session = sessionRef.current;
    const baseReport = reviewReport;
    setSaving(true);
    clearSaveError();
    setSkippedCount(0);
    trackProductEvent("cv_health_fix_started", {
      entry_point: ENTRY_POINT,
      scope: reviewScope,
      count_band: toCountBand(built.items.length),
      edited_count: built.items.filter((item) => item.text !== undefined).length,
    });

    const response = await applyCvHealthChanges({
      reportId: baseReport.id,
      items: built.items,
      trigger: "editor_apply",
    }).catch(() => null);
    savingRef.current = false;
    const result = response?.data ?? null;
    const current = session === sessionRef.current;
    if (!result) {
      // A fresh review replaced the report this review came from: nothing was
      // applied; say the CV's check moved on and fetch the current one.
      const replaced = response?.error?.code === CV_HEALTH_NOT_FOUND;
      if (replaced) refetchLatest();
      if (current) {
        setSaving(false);
        setSkippedErrorCount(replaced ? built.items.length : 0);
        setSaveError(replaced ? "skipped" : "saveFailed");
      }
      return;
    }
    if (result.receipt.items.length === 0) {
      // Nothing changed: the report is the base one (stale if the CV moved on,
      // which the automatic recheck picks up).
      writeLatest(result);
      if (current) {
        setSaving(false);
        setSkippedErrorCount(result.receipt.skipped.length);
        setSaveError(result.receipt.skipped.some((skip) => skip.reason === "stale") ? "skipped" : "saveFailed");
      }
      return;
    }
    for (const item of result.receipt.items) {
      trackProductEvent("cv_health_change_applied", {
        check_id: item.checkId,
        area: reviewFindings.find((entry) => entry.key === item.findingKey)?.area ?? null,
        target_kind: toCvHealthTargetKind(target),
      });
    }
    pendingFixRef.current = { receipt: result.receipt, baseReport };
    followServerWrite(result, "editor_apply");
    if (current) {
      setSkippedCount(result.receipt.skipped.length);
      settleSave(result, baseReport);
    }
  }, [
    clearSaveError,
    drafts,
    followServerWrite,
    refetchLatest,
    reviewFindings,
    reviewReport,
    reviewScope,
    settleSave,
    target,
    writeLatest,
  ]);

  /**
   * "Keep original": nothing is saved and nothing is decided, whichever way
   * the review was opened; the suggestions stay on the report. A row's own
   * "Leave unchanged" / "Skip for now" is the decision that stops asking.
   * The `keep_original` event stays what it always meant: one suggestion's
   * proposal turned down; a full or area review records none.
   */
  const keepOriginal = useCallback(() => {
    if (reviewScope === "single" && reviewFindings[0]) {
      trackFindingAction("keep_original", reviewFindings[0]);
    }
    leaveReview(false);
  }, [leaveReview, reviewFindings, reviewScope]);

  // ── One link (Add a work link) ───────────────────────────────────────
  const addProofLink = useCallback(
    (next: CvHealthFinding) => {
      if (!report || rechecking) return;
      trackFindingAction("add_link", next);
      setReviewReport(report);
      setFinding(buildProofLinkFinding(next));
      setLinkDraft(null);
      clearSaveError();
      setReturnView(view === "area" ? "area" : "overview");
      setView("proposal");
    },
    [clearSaveError, rechecking, report, view],
  );

  const handleApply = useCallback(
    async (text: string) => {
      const proposal = finding?.proposal;
      if (!finding || !proposal || !reviewReport || saving) return;
      const session = sessionRef.current;
      const baseReport = reviewReport;

      let value = text.trim();
      if (proposal.target.kind === "link") {
        const normalized = normalizeCvHealthLink(proposal.target.key, value);
        if (!normalized) {
          setSaveError("invalidLink");
          return;
        }
        value = normalized;
      }
      setSaving(true);
      setSaveError(null);

      const response = await applyCvHealthChanges({
        reportId: baseReport.id,
        items: [{ findingKey: finding.key, text: value }],
        trigger: "editor_apply",
      }).catch(() => null);
      const result = response?.data ?? null;
      const current = session === sessionRef.current;
      if (!result) {
        const replaced = response?.error?.code === CV_HEALTH_NOT_FOUND;
        if (replaced) refetchLatest();
        if (current) {
          setSkippedErrorCount(replaced ? 1 : 0);
          setSaveError(replaced ? "skipped" : "saveFailed");
          setSaving(false);
        }
        return;
      }
      if (result.receipt.items.length === 0) {
        writeLatest(result);
        if (current) {
          const stale = result.receipt.skipped.some((skip) => skip.reason === "stale");
          setSkippedErrorCount(result.receipt.skipped.length);
          setSaveError(stale ? "skipped" : "saveFailed");
          setSaving(false);
        }
        return;
      }

      trackProductEvent("cv_health_change_applied", {
        check_id: finding.checkIds[0] ?? null,
        area: finding.area,
        target_kind: toCvHealthTargetKind(target),
      });
      followServerWrite(result, "editor_apply");
      if (current) {
        setLinkDraft(null);
        setFinding(null);
        settleSave(result, baseReport);
      }
    },
    [finding, followServerWrite, refetchLatest, reviewReport, saving, settleSave, target, writeLatest],
  );

  // ── Undo: the server restores what the receipt changed ──────────────
  const handleUndo = useCallback(async () => {
    if (!receipt || receipt.undone || undoing) return;
    setUndoing(true);
    setUndoFailed(false);
    const response = await undoCvHealthChanges({
      reportId: receipt.receipt.fromReportId,
      receipt: receipt.receipt,
      trigger: "editor_undo",
    }).catch(() => null);
    const result = response?.data ?? null;
    if (!result) {
      setUndoing(false);
      setUndoFailed(true);
      return;
    }
    trackProductEvent("cv_health_fix_undone", {
      entry_point: ENTRY_POINT,
      applied_count: receipt.receipt.items.length,
    });
    pendingFixRef.current = null;
    followServerWrite(result, "editor_undo");
    setUndoing(false);
    // The undone CV's report is the one the change came from: the gauge moves back.
    // A change the CV moved on from since stays; the notice says how many.
    setReceipt((current) =>
      current
        ? {
            ...current,
            baseReport: report ?? current.baseReport,
            undone: true,
            undoSkipped: result.skipped.length,
          }
        : current,
    );
  }, [followServerWrite, receipt, report, undoing]);

  // ── Navigation ────────────────────────────────────────────────────────
  const openArea = useCallback((next: CvHealthArea) => {
    setArea(next);
    setView("area");
  }, []);

  const backToOverview = useCallback(() => {
    clearSaveError();
    setView("overview");
  }, [clearSaveError]);

  /** Back (header, Escape, "Overview"): one view back; edited link text asks first. The overview closes. */
  const stepBack = useCallback(async () => {
    if (saving) return;
    if (view === "review") {
      // A full review keeps its ticks and edits for "Continue reviewing"; a
      // single suggestion or an area's is a quick look, so edits there ask first.
      if (reviewScope !== "all" && reviewEdited && !(await confirmDiscard())) return;
      leaveReview(true);
      return;
    }
    if (view === "proposal") {
      if (isDirty && !(await confirmDiscard())) return;
      setLinkDraft(null);
      setFinding(null);
      clearSaveError();
      setView(returnView);
      return;
    }
    if (view === "area") {
      noteAreaBack();
      setView("overview");
      return;
    }
    onCloseRef.current();
  }, [
    clearSaveError,
    confirmDiscard,
    isDirty,
    leaveReview,
    returnView,
    reviewEdited,
    reviewScope,
    saving,
    view,
  ]);

  /** Close (X, Done, backdrop on web): edited text asks first. */
  const requestClose = useCallback(async () => {
    if (isDirty && !(await confirmDiscard())) return;
    onCloseRef.current();
  }, [confirmDiscard, isDirty]);

  /**
   * Back to the overview with nothing pending. Runs once the sheet is gone.
   * A running review keeps going (the dock follows it); a finished or failed
   * one is forgotten, so the next open reads the latest report afresh.
   */
  const reset = useCallback(() => {
    sessionRef.current += 1;
    savingRef.current = false;
    if (reviewPhaseRef.current !== "starting" && reviewPhaseRef.current !== "running") {
      resetReview();
    }
    setView("overview");
    setArea("human");
    setReturnView("overview");
    setReviewScope("all");
    setReviewFindings([]);
    setReviewReport(null);
    setDrafts([]);
    setReviewPending(false);
    setFinding(null);
    setLinkDraft(null);
    setSaving(false);
    setSaveError(null);
    setErrorFindingKey(null);
    setSkippedErrorCount(0);
    setSkippedCount(0);
    setReceipt(null);
    setUndoing(false);
    setUndoFailed(false);
    setFollowingWrite(false);
    setSuggestionsOpen(false);
    setCheckedOpen(false);
  }, [resetReview]);

  const reviewItems = useMemo(() => {
    const byKey = new Map(reviewFindings.map((entry) => [entry.key, entry]));
    return drafts.flatMap((draft) => {
      const entry = byKey.get(draft.findingKey);
      return entry ? [{ finding: entry, include: draft.include, text: draft.text }] : [];
    });
  }, [drafts, reviewFindings]);

  // The review's scope also holds suggestions with no ready change (each
  // needs a detail only the user has): the review says how many, so "Review N
  // suggestions" opening fewer changes is never a silent gap.
  const reviewOtherCount = useMemo(() => {
    if (!reviewReport || reviewScope === "single") return 0;
    const inReview = new Set(reviewFindings.map((entry) => entry.key));
    const area = reviewScope === "area" ? reviewFindings[0]?.area : undefined;
    return getSuggestionFindings(reviewReport, area).filter((entry) => !inReview.has(entry.key))
      .length;
  }, [reviewFindings, reviewReport, reviewScope]);

  /** "Show in the list" under a review: the overview, with the suggestion list open. */
  const showOtherSuggestions = useCallback(async () => {
    if (saving) return false;
    if (reviewScope !== "all" && reviewEdited && !(await confirmDiscard())) return false;
    leaveReview(true);
    setView("overview");
    setSuggestionsOpen(true);
    return true;
  }, [confirmDiscard, leaveReview, reviewEdited, reviewScope, saving]);

  return {
    // data
    health,
    report,
    overviewReport,
    showsResult,
    checkingReport,
    stage,
    overviewMode,
    fixableFindings,
    skippedCount,
    view,
    area,
    reviewScope,
    reviewItems,
    reviewOtherCount,
    selectedCount: countSelectedDrafts(drafts),
    reviewPending,
    finding,
    saving,
    saveError,
    errorFindingKey,
    errorNonce,
    skippedErrorCount,
    busyFindingKey,
    receipt,
    recheckStatus,
    rechecking,
    canUndo: receipt !== null && !receipt.undone,
    undoing,
    undoFailed,
    isDirty,
    suggestionsOpen,
    checkedOpen,
    // handlers
    handleCheck,
    handleRecheck,
    reviewAll,
    reviewArea,
    previewFinding,
    toggleDraft,
    setDraftText,
    saveReview,
    keepOriginal,
    showOtherSuggestions,
    handleApply,
    handleUndo,
    addProofLink,
    decide,
    openArea,
    setArea,
    setSuggestionsOpen,
    setCheckedOpen,
    backToOverview,
    setLinkDraft,
    stepBack,
    requestClose,
    reset,
  };
}

export type CvHealthSheetController = ReturnType<typeof useCvHealthSheetController>;
